import { useAppQuery } from '@/hooks/useAppQuery';
import { api } from '@/lib/api-client';
import type { Item } from '@/models/item/item';
import type { ItemRarity, ItemType } from '@/models/item/itemSchema';
import type { LifecycleState } from '@/models/lifecycleState';
import type { PaginationResponse } from '@/models/pagination';

import { itemsQueryKey } from './queryKeys';

export interface ItemsFilters {
  name?: string;
  item_rarity?: ItemRarity | 'all';
  type?: ItemType | 'all';
  page?: number;
  state?: LifecycleState | 'all';
}

/** ItemsFilters, where several types go out as repeated `type=` params. */
export type GetItemsFilters = Omit<ItemsFilters, 'type'> & {
  type?: ItemsFilters['type'] | ItemType[];
};

export const getItemsQueryOptions = (filters?: GetItemsFilters) => {
  return {
    queryKey: itemsQueryKey.filters(filters),
    queryFn: () => getItems(filters, 0, 1000),
  };
};

export const getItems = async (
  filters?: GetItemsFilters,
  offSet = 0,
  limit = 96,
) => {
  const queryParams = new URLSearchParams();
  queryParams.append('limit', limit.toString());
  queryParams.append('offset', offSet.toString());
  if (filters) {
    Object.entries(filters).forEach(
      ([key, value]: [string, string | number | string[] | undefined]) => {
        if (value === '' || value === 'all' || value == null) return;
        [value].flat().forEach((one) => {
          queryParams.append(key, String(one).toLowerCase());
        });
      },
    );
  }
  queryParams.append('sort_by', 'name');
  queryParams.append('sort_order', 'asc');

  const response = await api.get<{ items: Item[] } & PaginationResponse>(
    `/v1/items?${queryParams.toString()}`,
  );
  return response.data;
};

// `{}` by default: the key a loader fills with getItemsQueryOptions({}).
export const useGetItems = (filters: GetItemsFilters = {}) => {
  return useAppQuery({
    ...getItemsQueryOptions(filters),
  });
};

export const useGetSnapshotItems = (filters?: ItemsFilters) => {
  return useAppQuery({
    ...getItemsQueryOptions(filters),
    select: (data) => ({
      ...data,
      items: data.items.filter((item) => item.type !== 'currency'),
    }),
  });
};
